/* eslint-disable no-underscore-dangle */
import { AppLoader, AppLoaderConfig, AuthConfig, XV1App, XV1AuthenticateEvent, XV1NavigateEvent, XV2App } from '@nab/x-types';
import { XV2AppLoader } from './XV2AppLoader';

interface XV1CompatibleAuthConfig extends AuthConfig {
    errorCode?: string;
}

// interface KeepAliveAuthConfig extends AuthConfig {
//     errorCode?: string;
// }

/**
 * Loads nab-x custom element apps with backward compatibility for apps build before the XV2 interface was defined
 */
export class XV1AppLoader extends XV2AppLoader implements AppLoader<XV1App> {
    /**
     * Does the same thing as the XV2AppLoader but also registers DOM event handlers for backward compatibility
     *
     * @todo Were event subscriptions (aside from auth) ever implemented in the previous sdk app loader?? There doesn't seem to be any place where event listeners are registered on app elements for allowedSubscribedEvents apart from the auth events.
     * @todo appConfig supplied in it's previous form will need to be merged with new appConfig - this should be done when populating the registry with legacy configs
     * @todo apply authenticate 'hook' - see latest version of app loader for details
     * @todo KEEP_ALIVE event listener - Just call authenticate with keepAlive: true in config - may need a keep alive strategy in IB
     *
     */
    protected applyProps(config: Partial<AppLoaderConfig<XV1App>>) {
        const conf: Partial<AppLoaderConfig<XV1App>> = {
            appConfig: {},
            authConfig: <AuthConfig>{},
            ...config,
            appMetaData: {
                properties: {},
                ...config.appMetaData
            }
        };

        /**
         * Config and props in XV1 apps is all over the place
         * The same config is supplied in several different ways across different mini-apps hence
         * the need for all of these hacks
         */
        if (conf.appConfig && conf.appConfig.routingBasePath) {
            (<any>conf.appConfig).basename = conf.appConfig.routingBasePath;
        }
        if (conf.authConfig && conf.authConfig.environment) {
            (<any>conf.appConfig).environment = (<any>conf.appConfig).environment || conf.authConfig.environment;
            (<any>conf.appConfig).environmentName = (<any>conf.appConfig).environmentName || conf.authConfig.environment;
            conf.appMetaData.properties.environment = conf.appMetaData.properties.environment || conf.authConfig.environment;
        }

        super.applyProps(conf);

        // map the service instances to the app props for XV1 apps only
        if (config.services) {
            this.appElement.setAttribute('analyticsMgr', JSON.stringify(config.services.analyticsMgr));
            this.appElement.analyticsMgr = config.services.analyticsMgr;

            this.appElement.setAttribute('dataSecurity', JSON.stringify(config.services.dataSecurityMgr));
            this.appElement.dataSecurity = config.services.dataSecurityMgr;
        }

        // Create event listeners for interop actions. These are one-way
        if (config.actions) {
            this.appElement.addEventListener(`nabx:${config.appMetaData.tag}:navigate`, (e: CustomEvent<XV1NavigateEvent>) => {
                config.actions.navigate(e.detail.uri, e.detail.data);
            });

            // Invokes authenticate action function with forceRefresh flag and adds resulting token value to the auth prop of the app element
            const authEventListener: EventListener = async (e: CustomEvent<XV1AuthenticateEvent>) => {
                const authConfig: XV1CompatibleAuthConfig = <XV1CompatibleAuthConfig>{
                    ...(e.detail && e.detail.configOverrides),
                    errorCode: e.detail && e.detail.code
                };
                const authToken = await config.actions.authenticate<XV1CompatibleAuthConfig>(authConfig, e.detail.forceRefresh);
                // this.appElement.auth = authToken.tokenValue;
                this.appElement.setAttribute('auth', authToken.tokenValue);
            };

            const authKeepAliveListener: EventListener = () => {
                config.actions.authenticate({
                    keepAlive: true
                } as any);
            };

            // Used in AppLoader @todo which of these are actually used?
            this.appElement.addEventListener(`nabx:${config.appMetaData.tag}:authenticate`, authEventListener);
            this.appElement.addEventListener(`nabx:${config.appMetaData.tag}:request_token`, authEventListener);
            this.appElement.addEventListener(`request_token:${config.appMetaData.tag}`, authEventListener);
            this.appElement.addEventListener(`nabx:request_token:${config.appMetaData.tag}`, authEventListener);
            this.appElement.addEventListener(`nabx:request_token`, authEventListener);

            // Used in IB
            this.appElement.addEventListener(`nabx:${config.appMetaData.tag}:AUTH_REQUIRED`, authEventListener);
            this.appElement.addEventListener(`nabx:AUTH_REQUIRED`, authEventListener);
            this.appElement.addEventListener(`nabx:${config.appMetaData.tag}:KEEP_ALIVE`, authKeepAliveListener);
            this.appElement.addEventListener(`nabx:KEEP_ALIVE`, authKeepAliveListener);
        }
    }

    /**
     * Configures public path using the old App Loader style
     */
    protected configurePublicPath(): void {
        super.configurePublicPath();

        const window: Window = this.config.hostElement.ownerDocument.defaultView;
        const app: XV2App = this.config.appMetaData;

        window.__nabxAppLoader__ = window.__nabxAppLoader__ || {};
        window.__nabxAppLoader__.loadedApp = window.__nabxAppLoader__.loadedApp || {};
        if (!window.__nabxAppLoader__.loadedApp[this.config.appMetaData.id]) {
            window.__nabxAppLoader__.loadedApp[this.config.appMetaData.id] = {
                manifest: app.assetManifestUrl
            };
        }
    }

    /**
     * Invoke authentication and update auth attribute if successful but don't await the result
     */
    protected initAuth(): void {
        const {
            authConfig: { requestTokenOnLoad = true },
            actions: { authenticate } = {},
            appMetaData: { properties: { skipAuthOnRender = false } = {} }
        } = this.config;
        if (authenticate) {
            (async () => {
                try {
                    // for backwards compatibility we have to introduce this property,
                    // as the old app loader was supporting the authenticate function. #PR-208
                    this.appElement.authenticate = this.legacyAuthenticateCallback;
                    // If skipAuthOnRender is defined, always respect this before considering requestTokenOnLoad
                    if (!skipAuthOnRender && requestTokenOnLoad) {
                        const tokenValue = await this.legacyAuthenticateCallback();
                        this.appElement.setAttribute('auth', tokenValue);
                    }
                } catch (e) {
                    // Swallow rejection as we don't care about the result
                }
            })();
        }
    }

    /**
     * For backwards compatibility we have to introduce this property, as the old app loader was supporting the authenticate function. #PR-208
     * The params are just added to match the signature of old app loader authenticate function
     */
    private legacyAuthenticateCallback = async (tagName?: string, forceRefresh: boolean = true): Promise<string> => {
        const token = await this.config.actions.authenticate(null, forceRefresh);
        return token && token.tokenValue;
    };

    /**
     * Replicates previous behaviour of assigning properties as attributes rather than object properties
     */
    protected applyCustomProperties(config: Partial<AppLoaderConfig<XV2App>>) {
        super.applyCustomProperties(config);
        const propsApplied: string[] = [];
        for (const propName of Object.keys(config.appMetaData.properties)) {
            propsApplied.push(propName);
            let attr;
            if (propName.toLowerCase() === 'appconfig') {
                if (typeof config.appMetaData.properties[propName] === 'object') {
                    attr = {
                        ...config.appConfig,
                        ...(<Object>config.appMetaData.properties[propName])
                    };
                } else {
                    attr = <string>config.appMetaData.properties[propName];
                }
            } else {
                attr = config.appMetaData.properties[propName];
            }
            this.appElement[propName] = attr; // for setting objects/functions that needs to sent through to old app loader for compatibility
            this.appElement.setAttribute(propName, typeof attr === 'object' ? JSON.stringify(attr) : attr);
        }
        return propsApplied;
    }
}
